import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Eye, Check, X, Send, Trash2, Package, Clock, CheckCircle, XCircle, Truck, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateOnly } from '@/utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { purchaseOrdersAPI } from '@/services/api';
import { Checkbox } from '@/components/ui/checkbox';
import BulkDeleteBar from '@/components/hq/BulkDeleteBar';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  partially_received: { label: 'Partially Received', color: 'bg-purple-100 text-purple-700', icon: Package },
  fully_received: { label: 'Fully Received', color: 'bg-emerald-100 text-emerald-700', icon: Truck },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const PurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const { toast } = useToast();

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const response = await purchaseOrdersAPI.list(params);
      setPurchaseOrders(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch purchase orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, toast]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  const handleViewPO = (po) => {
    setSelectedPO(po);
    setViewDialogOpen(true);
  };

  const handleAction = async () => {
    try {
      switch (actionType) {
        case 'submit':
          await purchaseOrdersAPI.submit(selectedPO.id);
          toast({ title: 'Success', description: 'PO submitted for approval' });
          break;
        case 'approve':
          await purchaseOrdersAPI.approve(selectedPO.id);
          toast({ title: 'Success', description: 'PO approved successfully' });
          break;
        case 'reject':
          await purchaseOrdersAPI.reject(selectedPO.id, actionReason);
          toast({ title: 'Success', description: 'PO rejected' });
          break;
        case 'cancel':
          await purchaseOrdersAPI.cancel(selectedPO.id, actionReason);
          toast({ title: 'Success', description: 'PO cancelled' });
          break;
        default:
          break;
      }
      setActionDialogOpen(false);
      setActionReason('');
      fetchPurchaseOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Action failed', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    try {
      await purchaseOrdersAPI.delete(selectedPO.id);
      toast({ title: 'Success', description: 'Purchase Order deleted' });
      setDeleteDialogOpen(false);
      fetchPurchaseOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    }
  };

  const openActionDialog = (po, type) => {
    setSelectedPO(po);
    setActionType(type);
    setActionDialogOpen(true);
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Purchase Orders</h1>
          <p className="text-gray-500">Manage vendor purchase orders</p>
        </div>
        <Button 
          onClick={() => navigate('/hq/purchase-orders/new')} 
          className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-200" 
          data-testid="create-po-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Create PO
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search PO number, vendor..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-gray-300">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : purchaseOrders.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No purchase orders found</p>
            <Button onClick={() => navigate('/hq/purchase-orders/new')} variant="link" className="text-teal-600 mt-2">
              Create your first PO
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gray-200 shadow-sm">
          {selectedIds.length > 0 && (
            <div className="p-3 border-b border-gray-200">
              <BulkDeleteBar
                selectedIds={selectedIds}
                entityLabel="purchase order"
                endpoint="/hq/bulk-delete/purchase-orders"
                supportModes
                onCleared={() => { setSelectedIds([]); fetchPurchaseOrders(); }}
              />
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200">
                <TableHead className="w-10">
                  <Checkbox
                    checked={purchaseOrders.length > 0 && selectedIds.length === purchaseOrders.length}
                    onCheckedChange={(c) => setSelectedIds(c ? purchaseOrders.map(p => p.id) : [])}
                    data-testid="po-select-all"
                  />
                </TableHead>
                <TableHead className="text-gray-600">PO Number</TableHead>
                <TableHead className="text-gray-600">Vendor</TableHead>
                <TableHead className="text-gray-600">Warehouse</TableHead>
                <TableHead className="text-gray-600">Date</TableHead>
                <TableHead className="text-gray-600">Total</TableHead>
                <TableHead className="text-gray-600">Status</TableHead>
                <TableHead className="text-gray-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchaseOrders.map(po => {
                const StatusIcon = statusConfig[po.status]?.icon || FileText;
                return (
                  <TableRow key={po.id} className="border-gray-200 hover:bg-gray-50" data-testid={`po-row-${po.po_number}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(po.id)}
                        onCheckedChange={(c) => setSelectedIds(prev => c ? [...prev, po.id] : prev.filter(x => x !== po.id))}
                        data-testid={`po-select-${po.po_number}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-teal-600">{po.po_number}</TableCell>
                    <TableCell className="text-gray-700">{po.vendor_name}</TableCell>
                    <TableCell className="text-gray-600">{po.warehouse_name}</TableCell>
                    <TableCell className="text-gray-600">{formatDateOnly(po.order_date)}</TableCell>
                    <TableCell className="text-gray-700 font-medium">₹{po.grand_total?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[po.status]?.color} flex items-center gap-1 w-fit`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[po.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewPO(po)} title="View">
                          <Eye className="w-4 h-4 text-gray-500" />
                        </Button>
                        {po.status === 'draft' && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/hq/purchase-orders/${po.id}/edit`)} title="Edit">
                              <Edit2 className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openActionDialog(po, 'submit')} title="Submit for Approval">
                              <Send className="w-4 h-4 text-teal-500" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedPO(po); setDeleteDialogOpen(true); }} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {po.status === 'pending_approval' && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openActionDialog(po, 'approve')} title="Approve">
                              <Check className="w-4 h-4 text-emerald-500" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openActionDialog(po, 'reject')} title="Reject">
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {['draft', 'pending_approval', 'approved'].includes(po.status) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openActionDialog(po, 'cancel')} title="Cancel PO">
                            <XCircle className="w-4 h-4 text-gray-500" />
                          </Button>
                        )}
                        {(po.status === 'approved' || po.status === 'partially_received') && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/hq/grn/new?po=${po.id}`)} title="Create GRN">
                            <Package className="w-4 h-4 text-purple-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-3">
              Purchase Order: {selectedPO?.po_number}
              {selectedPO && (
                <Badge className={statusConfig[selectedPO.status]?.color}>
                  {statusConfig[selectedPO.status]?.label}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedPO && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="text-gray-500">Vendor</p>
                  <p className="font-medium text-gray-800">{selectedPO.vendor_name} ({selectedPO.vendor_code})</p>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-500">Warehouse</p>
                  <p className="font-medium text-gray-800">{selectedPO.warehouse_name}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-500">Order Date</p>
                  <p className="font-medium text-gray-800">{formatDateOnly(selectedPO.order_date)}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-500">Expected Delivery</p>
                  <p className="font-medium text-gray-800">
                    {selectedPO.expected_delivery_date ? formatDateOnly(selectedPO.expected_delivery_date) : '-'}
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-gray-200">
                      <TableHead className="text-gray-600">Product</TableHead>
                      <TableHead className="text-gray-600 text-center">Ordered</TableHead>
                      <TableHead className="text-gray-600 text-center">Received</TableHead>
                      <TableHead className="text-gray-600 text-right">Unit Price</TableHead>
                      <TableHead className="text-gray-600 text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPO.items?.map((item, i) => (
                      <TableRow key={i} className="border-gray-200">
                        <TableCell>
                          <p className="text-gray-800">{item.product_name}</p>
                          <p className="text-gray-500 text-xs">{item.product_sku}</p>
                        </TableCell>
                        <TableCell className="text-center text-gray-700">{item.ordered_qty}</TableCell>
                        <TableCell className="text-center text-gray-700">{item.received_qty || 0}</TableCell>
                        <TableCell className="text-right text-gray-700">₹{item.unit_price}</TableCell>
                        <TableCell className="text-right font-medium text-gray-800">₹{item.line_total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <div className="flex justify-end gap-6 text-sm">
                    <div>Subtotal: <span className="font-medium">₹{selectedPO.subtotal}</span></div>
                    <div>Discount: <span className="font-medium text-red-600">-₹{selectedPO.discount_total}</span></div>
                    <div>Tax: <span className="font-medium">₹{selectedPO.tax_total}</span></div>
                    <div className="font-bold text-teal-600">Total: ₹{selectedPO.grand_total}</div>
                  </div>
                </div>
              </div>

              {selectedPO.notes && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-gray-700 whitespace-pre-line">{selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">
              {actionType === 'submit' && 'Submit for Approval?'}
              {actionType === 'approve' && 'Approve Purchase Order?'}
              {actionType === 'reject' && 'Reject Purchase Order?'}
              {actionType === 'cancel' && 'Cancel Purchase Order?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              {actionType === 'submit' && `Submit ${selectedPO?.po_number} for approval. You won't be able to edit it after submission.`}
              {actionType === 'approve' && `Approve ${selectedPO?.po_number}. This will allow GRN creation against this PO.`}
              {actionType === 'reject' && `Reject ${selectedPO?.po_number}. The PO will be returned to draft status.`}
              {actionType === 'cancel' && `Cancel ${selectedPO?.po_number}. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {(actionType === 'reject' || actionType === 'cancel') && (
            <div className="py-2">
              <Label className="text-gray-700">Reason (optional)</Label>
              <Textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter reason..." className="mt-2 bg-gray-50 border-gray-300" />
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}
              className={actionType === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : 
                actionType === 'submit' ? 'bg-teal-500 hover:bg-teal-600' : 'bg-red-500 hover:bg-red-600'}>
              {actionType === 'submit' && 'Submit'}
              {actionType === 'approve' && 'Approve'}
              {actionType === 'reject' && 'Reject'}
              {actionType === 'cancel' && 'Cancel PO'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Delete Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will delete "{selectedPO?.po_number}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchaseOrdersPage;
